import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useResource, useFetcher } from "rest-hooks";
import { EncumberanceCheckResource, SessionEncumberanceCheckResource } from "./EncumberanceCheckResource";
import { regNoWarnings } from "./encumberanceCheckMessages";
import { useIntl } from "react-intl";
import { IsRegNoEqual, NormalizeRegNo } from "./EncumberanceCheckParts";
import { willResetMval96 } from "./mval96";
const toEditableFields = (object) => ({
    regNo: object?.regNo ?? "",
    chassisNo: object?.chassisNo ?? "",
    machineRegNo: object?.machineRegNo ?? "",
    deliveryDate: object?.deliveryDate ?? "",
    identificationCode: object?.identificationCode ?? "",
    objectNameDescription: object?.objectNameDescription ?? "",
    portOfRegistry: object?.portOfRegistry ?? ""
});
function useEncumberanceCheckValue({ applicationId, isSession, canResetDocs, initialState, onPersist }) {
    const EncumberanceResource = isSession ? SessionEncumberanceCheckResource : EncumberanceCheckResource;
    const params = isSession ? {} : { applicationId: applicationId };
    const currentObject = useResource(EncumberanceResource.detail(), params);
    const searchByRegNo = useFetcher(EncumberanceResource.searchByRegNo());
    const searchByChassisNo = useFetcher(EncumberanceResource.searchByChassisNo());
    const checkEncumberance = useFetcher(EncumberanceResource.checkEncumberance());
    const checkTitle = useFetcher(EncumberanceResource.titleCheck());
    const save = useFetcher(EncumberanceResource.save());
    const refetchCurrentObject = useFetcher(EncumberanceResource.detail());
    const intl = useIntl();
    const [searchError, setSearchError] = useState(initialState?.searchError);
    const [searchedRegNo, setSearchedRegNo] = useState(initialState?.searchedRegNo ?? "");
    const [searchResult, setSearchResult] = useState(initialState?.searchResult);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fields, setFields] = useState(() => initialState?.fields ?? toEditableFields(currentObject.object));
    // Keep state between form pages
    const persist = useRef();
    persist.current = () => onPersist?.({ fields, searchError, searchedRegNo, searchResult });
    useEffect(() => () => persist.current?.(), []);
    const isVisible = (key) => currentObject.fieldVisibility?.[key] ?? false;
    const setField = (key) => (value) => setFields(prev => ({ ...prev, [key]: value }));
    const regNoValidationRegex = useMemo(() => {
        const pattern = currentObject.regNoRules?.validationPattern;
        if (!pattern)
            return null;
        try {
            return new RegExp(pattern);
        }
        catch {
            return null;
        }
    }, [currentObject.regNoRules?.validationPattern]);
    const isRegNoValid = (regNo) => {
        if (!regNoValidationRegex)
            return true;
        return regNoValidationRegex.test(regNo);
    };
    const doRegNoSearch = async (onSuccess, onFailure) => {
        setBusy(true);
        setSearchError(undefined);
        try {
            const result = await searchByRegNo({ ...params, regNo: fields.regNo });
            if (result?.error) {
                if (result?.error === "GeneralError") {
                    setSearchError({ regNo: fields.regNo });
                }
                setSearchResult(undefined);
                await onFailure(result.error);
            }
            else {
                setSearchResult(result);
                setSearchedRegNo(fields.regNo);
                if (result?.object?.chassisNo)
                    setField("chassisNo")(result?.object?.chassisNo);
                await onSuccess(result);
            }
        }
        catch {
            setSearchResult(undefined);
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doChassisNoSearch = async (onSuccess, onFailure) => {
        setBusy(true);
        try {
            const result = await searchByChassisNo({ ...params, chassisNo: fields.chassisNo });
            if (result?.error) {
                setSearchResult(undefined);
                await onFailure(result.error);
            }
            else {
                setSearchResult(result);
                if (result?.object?.regNo && isVisible("regNo")) {
                    setField("regNo")(result.object.regNo);
                    setSearchedRegNo(result.object.regNo);
                }
                await onSuccess(result);
            }
        }
        catch {
            setSearchResult(undefined);
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doEncumberanceCheck = async (onSuccess, onFailure) => {
        setBusy(true);
        try {
            const result = await checkEncumberance(params);
            const updated = await refetchCurrentObject(params);
            setFields(toEditableFields(updated?.object));
            if (result?.error)
                await onFailure(result.error);
            else
                await onSuccess(result);
        }
        catch {
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doTitleCheck = async (onSuccess, onFailure) => {
        setBusy(true);
        try {
            const result = await checkTitle(params);
            const updated = await refetchCurrentObject(params);
            setFields(toEditableFields(updated?.object));
            if (result?.error)
                await onFailure(result.error);
            else
                await onSuccess(result);
        }
        catch {
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doSave = async (onPreSave, onPostSave, onSuccess, onFailure) => {
        setBusy(true);
        setSaving(true);
        try {
            if (!await onPreSave())
                return;
            const result = await save(params, fields);
            if (!await onPostSave(result))
                return;
            const updated = await refetchCurrentObject(params);
            setFields(toEditableFields(updated?.object));
            if (result?.error) {
                await onFailure(result.error);
            }
            else {
                setSearchResult(prev => prev ? { ...prev, mismatch: result?.mismatch, mustReplace: result?.mustReplace } : prev);
                await onSuccess(result);
            }
        }
        catch {
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
            setSaving(false);
        }
    };
    const applicationRegNo = NormalizeRegNo(currentObject.object?.regNo ?? "");
    const signedForRegNo = NormalizeRegNo(currentObject.regNoRules?.signedForRegNo ?? "");
    const signedForChassisNo = NormalizeRegNo(currentObject.regNoRules?.signedForChassisNo ?? "");
    const regNoChanged = isVisible("regNo") && !IsRegNoEqual(fields.regNo, applicationRegNo);
    const chassisNoChanged = isVisible("chassisNo") && !IsRegNoEqual(fields.chassisNo, currentObject.object?.chassisNo ?? "") && signedForChassisNo.length > 0;
    const regNoCannotBeEmpty = fields.regNo.length === 0 && signedForRegNo.length > 0;
    const docsWillResetOnChange = signedForRegNo.length > 0 || chassisNoChanged;
    const docsWillResetOnSave = docsWillResetOnChange || willResetMval96(searchResult?.mismatch);
    const docsWillReset = ((regNoChanged && !IsRegNoEqual(fields.regNo, signedForRegNo)) || (chassisNoChanged && !IsRegNoEqual(fields.chassisNo, signedForChassisNo))) && docsWillResetOnSave;
    const hasSearchError = searchError !== undefined && IsRegNoEqual(fields.regNo, searchError.regNo);
    const searchValidationDisabled = currentObject.regNoRules?.searchValidationDisabled ?? false;
    const effectiveMustReplace = searchResult?.mustReplace ?? currentObject.mustReplace ?? false;
    const regNoSearchMissing = isVisible("regNoSearch") && !regNoCannotBeEmpty && fields.regNo.length > 0 && regNoChanged && !IsRegNoEqual(fields.regNo, searchedRegNo);
    const regNoNotValidated = isVisible("regNo") &&
        !regNoChanged &&
        !effectiveMustReplace &&
        !IsRegNoEqual(fields.regNo, searchedRegNo) &&
        currentObject.regNoSearchResult?.validationSuccess === false;
    const warnings = regNoWarnings(intl, {
        currentObject,
        searchResult,
        regNoEmpty: regNoCannotBeEmpty,
        regNoSearchMissing,
        searchValidationDisabled,
        regNoNotValidated,
        docsWillReset,
        docsWillResetOnSave,
        regNoChanged,
        searchedRegNo,
        regNoInput: fields.regNo,
        canResetDocs,
        hasSearchError
    });
    return {
        applicationId,
        currentObject,
        searchResult,
        isVisible,
        isRegNoValid,
        isBusy: busy,
        isSaving: saving,
        fields,
        setField,
        doRegNoSearch,
        doChassisNoSearch,
        doEncumberanceCheck,
        doTitleCheck,
        doSave,
        warnings,
        setSearchResult,
        regNoRules: {
            regNoChanged,
            chassisNoChanged,
            regNoCannotBeEmpty,
            regNoSearchMissing: regNoSearchMissing && !searchValidationDisabled && !hasSearchError,
            docsWillResetOnChange,
            docsWillResetOnSave,
            illegalObjectType: searchResult?.illegalObjectType
        }
    };
}
const EncumberanceCheckContext = createContext(undefined);
export const useEncumberanceCheckContext = () => {
    const context = useContext(EncumberanceCheckContext);
    if (!context) {
        throw new Error("useEncumberanceCheckContext must be used within an EncumberanceCheckProvider");
    }
    return context;
};
export const EncumberanceCheckContextProvider = ({ children, ...props }) => {
    return (_jsx(EncumberanceCheckContext.Provider, { value: useEncumberanceCheckValue(props), children: children }));
};
//# sourceMappingURL=EncumberanceCheckContext.js.map